import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { Routes } from "~/routes";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { FilterMetadata, QueryResponse } from "~/modules/filters/types";
import { queryAssets } from "~/modules/assets/api/router";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { Asset } from "~/modules/assets/types";
import {
    AssetScreenerStats,
    queryAssetScreenerStats,
} from "~/modules/asset_screener/api/queryAssetScreenerStats";
import { queryAssetScreenerFilterMetadata } from "~/modules/asset_screener/api/queryAssetScreenerFilterMetadata";
import {
    ASSET_SCREENER_DEFAULT_QUERY,
    DEAL_ROOM_ASSETS_QUERY,
} from "~/modules/asset_screener/consts";
import { AssetScreenerPageLayout } from "~/modules/asset_screener/pages/list";
import {
    AssetScreenerSEO,
    getAssetScreenerPageDescription,
} from "~/modules/app/seo/AssetScreenerSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";

// // // //

interface AssetScreenerPageProps {
    initialState: QueryResponse<Asset>;
    dealroomAssets: Asset[];
    filterMetadata: FilterMetadata[];
    stats: AssetScreenerStats;
}

export async function getStaticProps() {
    const [initialState, dealroomAssets, filterMetadata, stats] =
        await Promise.all([
            queryAssets({ query: ASSET_SCREENER_DEFAULT_QUERY }),
            queryAssets({ query: DEAL_ROOM_ASSETS_QUERY }),
            queryAssetScreenerFilterMetadata(),
            queryAssetScreenerStats(),
        ]);

    return {
        props: {
            initialState,
            dealroomAssets: dealroomAssets.results,
            filterMetadata,
            stats,
        } satisfies AssetScreenerPageProps,
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function AssetScreenerPage(props: AssetScreenerPageProps) {
    const { initialState, dealroomAssets, filterMetadata, stats } = props;

    return (
        <PageTrack route={Routes.assetScreener}>
            <NextSeo
                title="Asset Screener"
                canonical={addBaseUrl(Routes.assetScreener)}
                description={getAssetScreenerPageDescription()}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.assetScreener),
                    title: "Asset Screener",
                    site_name: "RWA.xyz",
                    description: getAssetScreenerPageDescription(),
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
            />
            <AssetScreenerSEO />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Asset Screener",
                        href: Routes.assetScreener,
                    },
                ]}
            />

            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.assetScreener} user={user} />
                )}
            </UserFetcher>

            <SidebarLayout pathname={Routes.assetScreener}>
                <PageWrapper>
                    <AssetScreenerPageLayout
                        initialState={initialState}
                        dealroomAssets={dealroomAssets}
                        filterMetadata={filterMetadata}
                        stats={stats}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
